import { ReactNode } from 'react'
import { NameValueDatum } from '../../types/mutual-fund-detail.types'
import AnimatedAccordion from './AnimatedAccordion'

type InPlaceNameValueAccordionPropTypes = {
  isExpanded: boolean
  onAccordionClick: () => void
  accordionClassName?: string
  chevronColor?: string
  alwaysShowValue?: boolean
  /** Details_B18: optional ≥24px touch target without changing layout elsewhere. */
  buttonClassName?: string
  children: ReactNode
} & NameValueDatum

export default function InPlaceNameValueAccordion({
  name,
  value,
  isExpanded,
  onAccordionClick,
  accordionClassName,
  chevronColor,
  alwaysShowValue,
  nameClassName,
  valueClassName,
  accessibilityLabel,
  buttonClassName,
  children
}: InPlaceNameValueAccordionPropTypes) {
  /*
   * Group label + value for TalkBack (Details_B08/B18). State comes from aria-expanded only —
   * do not put expanded/collapsed or “double tap to …” in the name (ViewBreakup_B08 / B10).
   * Hint lives only in aria-description (Details_B06 / Result_B07) to avoid double announcement.
   */
  const accordionLabel = accessibilityLabel ?? `${name.trim()}, ${value ?? '-'}`
  const accordionHint = isExpanded ? 'Double tap to collapse' : 'Double tap to expand'

  return (
    <div
      className={`${isExpanded ? 'pt-[10px] pb-[16px] mb-1 px-[12px] bg-[#EBF2F2] rounded-[12px]' : ''} flex flex-col text-[14px] w-full ${accordionClassName ?? ''}  `}
    >
      {children ? (
        <button
          type='button'
          onClick={onAccordionClick}
          aria-expanded={isExpanded}
          aria-label={accordionLabel}
          aria-description={accordionHint}
          className={`flex w-full justify-between border-none bg-transparent p-0 text-left cursor-pointer items-center ${buttonClassName ?? ''}`}
        >
          <div
            aria-hidden='true'
            className={`flex gap-[4px] ${isExpanded ? 'font-[500]' : 'text-[#6A6E6E]'} items-center ${nameClassName ?? ''}`}
          >
            <div>{name}</div>
            <div
              className={`w-[10px] h-[10px] ${isExpanded ? 'ml-[2px]' : 'mt-[2px]'}`}
              style={{
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <DownChevron chevronColor={chevronColor} />
            </div>
          </div>
          {(alwaysShowValue || !isExpanded) && (
            <div aria-hidden='true' className={`font-[500] ${valueClassName ?? ''}`}>
              {value}
            </div>
          )}
        </button>
      ) : (
        <div className='flex justify-between'>
          <div
            className={`flex gap-[4px] ${isExpanded ? 'font-[500]' : 'text-[#6A6E6E]'} items-center ${nameClassName ?? ''}`}
          >
            <div>{name}</div>
          </div>
          {(alwaysShowValue || !isExpanded) && <div className={`font-[500] ${valueClassName ?? ''}`}>{value}</div>}
        </div>
      )}
      <AnimatedAccordion isExpanded={isExpanded} className='text-[14px] leading-[150%]'>
        {children}
      </AnimatedAccordion>
    </div>
  )
}

const DownChevron = ({ chevronColor }: { chevronColor?: string }) => {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='12' height='10' viewBox='0 0 12 10' fill='none'>
      <path
        d='M2 3L6 7L10 3'
        stroke={chevronColor ?? '#6A6E6E'}
        strokeWidth='1.5'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </svg>
  )
}
